import React, { useEffect, useMemo, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime } from "@/lib/format";
import { FileText, Download, Calendar, Search, Receipt } from "lucide-react";
import { CategoryChipSelector } from "@/components/CategoryManager";
import InvoiceCheckArchive from "@/components/InvoiceCheckArchive";

const MONTH_NAMES_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const COMMODITY_LABEL = { gas: "Gas", power: "Power", both: "Gas + Power" };

function ReportCard({ r, onDownload }) {
  return (
    <div className="card-elevated p-5 hover:border-blue-300 transition-colors" data-testid={`custom-report-${r.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <FileText size={20} className="text-blue-600 flex-shrink-0" />
        {r.category ? (
          <span className="badge-neutral !bg-blue-50 !text-blue-700 text-[10px] font-semibold" data-testid={`cr-card-category-${r.id}`}>
            {r.category}
          </span>
        ) : (
          <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Riservato</span>
        )}
      </div>
      <div className="font-display font-bold text-slate-900 leading-tight">{r.title}</div>
      {r.description && <div className="text-sm text-slate-500 mt-1 line-clamp-2">{r.description}</div>}
      <div className="text-xs text-slate-400 mt-3 flex items-center gap-1">
        <Calendar size={11} /> {fmtDateTime(r.created_at)}
      </div>
      <button
        onClick={() => onDownload(r.id, r.filename)}
        className="btn-brand w-full mt-3 text-sm !py-2 flex items-center justify-center gap-1.5"
        data-testid={`custom-report-download-${r.id}`}
      >
        <Download size={14} /> Scarica PDF
      </button>
    </div>
  );
}

function InvoiceCheckCard({ r, onDownload }) {
  const period = r.billing_year && r.billing_month
    ? `${MONTH_NAMES_IT[r.billing_month - 1]} ${r.billing_year}`
    : "";
  return (
    <div className="card-elevated p-5 border-2 border-purple-100 hover:border-purple-400 transition-colors" data-testid={`invoice-check-${r.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <Receipt size={20} className="text-purple-600 flex-shrink-0" />
        {r.commodity && (
          <span className="badge-neutral !bg-purple-100 !text-purple-700 text-[10px] font-semibold uppercase" data-testid={`invoice-commodity-${r.id}`}>
            {COMMODITY_LABEL[r.commodity] || r.commodity}
          </span>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-purple-600 mb-1">{period}</div>
      <div className="font-display font-bold text-slate-900 leading-tight">{r.title}</div>
      {r.pod && <div className="text-xs text-slate-500 mt-1 font-mono">POD/PDR: {r.pod}</div>}
      {r.description && <div className="text-sm text-slate-500 mt-1 line-clamp-2">{r.description}</div>}
      <div className="text-xs text-slate-400 mt-3 flex items-center gap-1">
        <Calendar size={11} /> {fmtDateTime(r.created_at)}
      </div>
      <button
        onClick={() => onDownload(r.id, r.filename)}
        className="w-full mt-3 text-sm py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold flex items-center justify-center gap-1.5"
        data-testid={`invoice-check-download-${r.id}`}
      >
        <Download size={14} /> Scarica PDF
      </button>
    </div>
  );
}

export default function CustomReports() {
  const [reports, setReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [q, setQ] = useState("");
  const [loaded, setLoaded] = useState(false);
  // Filtro periodo per il riquadro Controllo Fatture: chiave "YYYY-MM" oppure "" = tutti
  const [invoicePeriod, setInvoicePeriod] = useState("");
  const [pdpLabels, setPdpLabels] = useState({});  // mappa POD/PDR → etichetta
  const [pdpAddresses, setPdpAddresses] = useState({});  // mappa POD/PDR → indirizzo fornitura
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([api.get("/custom-reports"), api.get("/custom-reports/categories")]);
        setReports(r.data);
        setCategories(c.data);
      } finally {
        setLoaded(true);
      }
      // Carica anche i PdP censiti dell'azienda per mostrare le etichette
      if (user?.company_id) {
        try {
          const { data } = await api.get(`/companies/${user.company_id}/pdps`);
          const labels = {};
          const addresses = {};
          for (const p of data || []) {
            const code = (p.code || "").toUpperCase();
            if (!code) continue;
            if (p.label) labels[code] = p.label;
            if (p.address) addresses[code] = p.address;
          }
          setPdpLabels(labels);
          setPdpAddresses(addresses);
        } catch (_e) { /* skip */ }
      }
    })();
  }, [user?.company_id]);

  // Split reports: invoice-check vs generic
  const invoiceReports = useMemo(() => reports.filter((r) => r.is_invoice_check), [reports]);
  const genericReports = useMemo(() => reports.filter((r) => !r.is_invoice_check), [reports]);

  // Categorie da mostrare nella chip-bar: escludiamo "Controllo Fatture" (ha già il suo riquadro)
  const genericCategories = useMemo(
    () => categories.filter((c) => (c.name || "").toLowerCase() !== "controllo fatture"),
    [categories],
  );

  // Elenco periodi disponibili per i controlli fatture (ordinato desc), con count
  const invoicePeriods = useMemo(() => {
    const map = new Map();
    for (const r of invoiceReports) {
      if (r.billing_year && r.billing_month) {
        const key = `${r.billing_year}-${String(r.billing_month).padStart(2, "0")}`;
        map.set(key, (map.get(key) || 0) + 1);
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([key, count]) => {
        const [y, m] = key.split("-");
        return { key, count, label: `${MONTH_NAMES_IT[Number(m) - 1]} ${y}` };
      });
  }, [invoiceReports]);

  const filteredInvoices = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return invoiceReports.filter((r) => {
      if (invoicePeriod) {
        const key = r.billing_year && r.billing_month
          ? `${r.billing_year}-${String(r.billing_month).padStart(2, "0")}`
          : "";
        if (key !== invoicePeriod) return false;
      }
      if (qLower) {
        const hay = [r.title, r.description, r.filename, r.pod, r.commodity].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(qLower)) return false;
      }
      return true;
    });
  }, [invoiceReports, invoicePeriod, q]);

  const filteredGeneric = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return genericReports.filter((r) => {
      if (activeCat && (r.category || "").toLowerCase() !== activeCat.toLowerCase()) return false;
      if (qLower) {
        const hay = [r.title, r.description, r.filename, r.category].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(qLower)) return false;
      }
      return true;
    });
  }, [genericReports, activeCat, q]);

  const download = (id, filename) => {
    const url = `${API}/custom-reports/${id}/download?auth=${encodeURIComponent(getToken() || "")}`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = filename || "report.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
          Report Personalizzati
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Report riservati alla tua azienda, preparati dal Desk Adybo.
        </p>
      </div>

      <div className="card-elevated p-4 flex flex-wrap gap-2 items-center" data-testid="cr-search-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca report…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
            data-testid="cr-search"
          />
        </div>
      </div>

      {/* Riquadro dedicato: Controllo Fatture — ora in formato tabellare con filtri + ZIP */}
      {invoiceReports.length > 0 && (
        <InvoiceCheckArchive reports={invoiceReports} scope="client" pdpLabels={pdpLabels} pdpAddresses={pdpAddresses} />
      )}

      {/* Sezione report generici (con chip categorie) */}
      {(genericReports.length > 0 || (invoiceReports.length === 0 && loaded)) && (
        <>
          {genericReports.length > 0 && (
            <div className="pt-2">
              <h2 className="font-display text-lg font-bold text-slate-900 mb-2">Altri Report Personalizzati</h2>
              <CategoryChipSelector
                categories={genericCategories}
                activeCat={activeCat}
                onChange={setActiveCat}
                totalCount={genericReports.length}
                testId="cr-cat-chips"
              />
            </div>
          )}
          {loaded && filteredGeneric.length === 0 && genericReports.length === 0 && invoiceReports.length === 0 ? (
            <div className="card-elevated p-10 text-center">
              <FileText size={40} className="mx-auto text-slate-300 mb-3" />
              <div className="text-slate-500">Non ci sono ancora report personalizzati disponibili.</div>
              <div className="text-xs text-slate-400 mt-1">Contatta il tuo referente Adybo per richiederne uno.</div>
            </div>
          ) : filteredGeneric.length === 0 && genericReports.length > 0 ? (
            <div className="card-elevated p-10 text-center">
              <div className="text-slate-500">Nessun report corrisponde ai filtri correnti.</div>
            </div>
          ) : (
            filteredGeneric.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredGeneric.map((r) => (
                  <ReportCard key={r.id} r={r} onDownload={download} />
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
